import { createContext, useContext } from 'react';

import type { ProgramHome_EnrolledProduct } from '__generated__/graphql-types-do-not-use';
import type { ApolloQueryResult } from 'apollo-client';

import type {
  EnterpriseBadgeCollectionsQuery as EnterpriseBadgeCollectionsQueryData,
  EnterpriseBadgeCollectionsQueryVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/EnterpriseBadgeCollectionsQuery';
import type { EnterpriseBadge } from 'bundles/page-config-common/providers/enterprise-skills/EnterpriseBadgeCollectionsProvider';
import type {
  EnterpriseEnrolledProductsQuery as EnterpriseEnrolledProductsQueryType,
  EnterpriseEnrolledProductsQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/EnterpriseEnrolledProductsQuery';

export type EnterpriseUserEnrolledProductsContextType = {
  enrolledProducts: Array<ProgramHome_EnrolledProduct>;
  enrolledBadgeCollections: Array<EnterpriseBadge>;
  issuedBadgeCollections: Array<EnterpriseBadge>;
  loading: boolean;
  totalEnrolledProducts: number;
  refetchEnterpriseBadgeCollections?: (
    variables?: EnterpriseBadgeCollectionsQueryVariables
  ) => Promise<ApolloQueryResult<EnterpriseBadgeCollectionsQueryData>>;
  refetchEnterpriseEnrolledProducts?: (
    variables?: EnterpriseEnrolledProductsQueryVariables
  ) => Promise<ApolloQueryResult<EnterpriseEnrolledProductsQueryType>>;
  handleViewMoreEnrolledProducts?: () => void;
  isFetchingMore: boolean;
};

const EnterpriseUserEnrolledProductsContext = createContext<EnterpriseUserEnrolledProductsContextType>({
  enrolledProducts: [],
  enrolledBadgeCollections: [],
  issuedBadgeCollections: [],
  loading: false,
  totalEnrolledProducts: 0,
  refetchEnterpriseBadgeCollections: undefined,
  refetchEnterpriseEnrolledProducts: undefined,
  handleViewMoreEnrolledProducts: undefined,
  isFetchingMore: false,
});

/**
 * @description The hook is used to access the user's enrolled products and badge collections.
 */
export const useEnterpriseUserEnrolledProducts = () => useContext(EnterpriseUserEnrolledProductsContext);

export default EnterpriseUserEnrolledProductsContext;
